import { useCallback, useEffect, useState } from 'react';
import {
  deleteSiteContextLayer,
  getEditor,
  listSiteContextLayers,
  resolveSelectedSiteContextLayerId,
  selectSiteContextLayer,
  toggleSiteContextLayerVisibility,
  type SiteContextLayer,
} from 'agentable-canvas/whiteboard';
import type { TLShapeId } from 'tldraw';

const MAX_BIND_ATTEMPTS = 80;
const BIND_POLL_MS = 100;

export interface UseSiteContextLayersResult {
  layers: SiteContextLayer[];
  editorReady: boolean;
  selectedShapeId: TLShapeId | null;
  selectLayer: (shapeId: SiteContextLayer['shapeId']) => void;
  toggleVisibility: (shapeId: SiteContextLayer['shapeId']) => void;
  deleteLayer: (shapeId: SiteContextLayer['shapeId'], layerName: string) => void;
}

/**
 * Subscribe to panel shapes inside the active site's context frame for the
 * sidebar CONTEXT layers list, with bidirectional canvas selection sync.
 */
export function useSiteContextLayers(
  siteId: string | null,
  enabled: boolean,
): UseSiteContextLayersResult {
  const [layers, setLayers] = useState<SiteContextLayer[]>([]);
  const [selectedShapeId, setSelectedShapeId] = useState<TLShapeId | null>(null);
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    if (!enabled || !siteId) {
      setLayers([]);
      setSelectedShapeId(null);
      setEditorReady(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribeDocument: (() => void) | undefined;
    let unsubscribeSession: (() => void) | undefined;
    let attempts = 0;

    const bind = (): boolean => {
      const editor = getEditor();
      if (!editor) return false;

      const refreshLayers = (): void => {
        if (cancelled) return;
        setLayers(listSiteContextLayers(editor, siteId));
      };

      const refreshSelection = (): void => {
        if (cancelled) return;
        setSelectedShapeId(resolveSelectedSiteContextLayerId(editor, siteId));
      };

      refreshLayers();
      refreshSelection();
      unsubscribeDocument = editor.store.listen(refreshLayers, { scope: 'document' });
      unsubscribeSession = editor.store.listen(refreshSelection, { scope: 'session' });
      setEditorReady(true);
      return true;
    };

    if (bind()) {
      return () => {
        cancelled = true;
        unsubscribeDocument?.();
        unsubscribeSession?.();
      };
    }

    const pollTimer = window.setInterval(() => {
      if (cancelled) return;
      attempts += 1;
      if (bind() || attempts >= MAX_BIND_ATTEMPTS) {
        window.clearInterval(pollTimer);
      }
    }, BIND_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(pollTimer);
      unsubscribeDocument?.();
      unsubscribeSession?.();
    };
  }, [siteId, enabled]);

  const selectLayer = useCallback(
    (shapeId: SiteContextLayer['shapeId']) => {
      if (!siteId) return;
      const editor = getEditor();
      if (!editor) return;
      selectSiteContextLayer(editor, shapeId, siteId);
    },
    [siteId],
  );

  const toggleVisibility = useCallback((shapeId: SiteContextLayer['shapeId']) => {
    const editor = getEditor();
    if (!editor) return;
    toggleSiteContextLayerVisibility(editor, shapeId);
  }, []);

  const deleteLayer = useCallback(
    (shapeId: SiteContextLayer['shapeId'], layerName: string) => {
      if (!siteId) return;
      const confirmed = window.confirm(
        `Delete layer "${layerName}"? This removes the panel from the canvas.`,
      );
      if (!confirmed) return;

      const editor = getEditor();
      if (!editor) return;
      deleteSiteContextLayer(editor, shapeId, siteId);
    },
    [siteId],
  );

  return { layers, editorReady, selectedShapeId, selectLayer, toggleVisibility, deleteLayer };
}
